import React, { useState, useEffect, useRef } from 'react';

interface MultiSelectDropdownProps {
    label: string;
    placeholder?: string;
    options: string[];
    selectedOptions: string[];
    onSelectionChange: (selectedOptions: string[]) => void;
    disabled?: boolean;
    loading?: boolean;
    description?: string;
    errorMessage?: string;
    allOption?: string;
    /** Make “All …” mutually exclusive (default true). */
    enforceExclusiveAll?: boolean;
    displayTransform?: (value: string) => string;
}

const defaultPretty = (opt: string) => {
    if (opt === 'AllClients') return 'All Clients';
    if (opt === 'AllDiagnosticCodes') return 'All Diagnostic Codes';
    if (opt === 'AllProcessors') return 'All Processors';
    return opt;
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
                                                                     label,
                                                                     placeholder = 'Select options...',
                                                                     options,
                                                                     selectedOptions,
                                                                     onSelectionChange,
                                                                     disabled = false,
                                                                     loading = false,
                                                                     description,
                                                                     errorMessage,
                                                                     allOption,
                                                                     enforceExclusiveAll = true,
                                                                     displayTransform,
                                                                 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showAllTags, setShowAllTags] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const pretty = displayTransform ?? defaultPretty;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const isAll = (opt: string) => allOption && opt === allOption;
    const isAllSelected = !!allOption && selectedOptions.includes(allOption);
    const hasAnyNonAll = selectedOptions.some(o => !isAll(o));

    const optionDisabled = (opt: string) => {
        if (!enforceExclusiveAll || !allOption) return false;
        if (isAll(opt)) return hasAnyNonAll;
        return isAllSelected;
    };

    const handleOptionToggle = (option: string) => {
        if (disabled || optionDisabled(option)) return;

        const currentlySelected = selectedOptions.includes(option);

        if (enforceExclusiveAll && allOption) {
            if (isAll(option)) {
                onSelectionChange(currentlySelected ? [] : [allOption]);
                return;
            }
            const withoutAll = selectedOptions.filter(o => o !== allOption);
            onSelectionChange(currentlySelected ? withoutAll.filter(o => o !== option) : [...withoutAll, option]);
            return;
        }

        onSelectionChange(
            currentlySelected ? selectedOptions.filter(item => item !== option) : [...selectedOptions, option]
        );
    };

    const handleClearAll = () => onSelectionChange([]);

    const tagsToShow = showAllTags ? selectedOptions : selectedOptions.slice(0, 3);
    const hasMoreTags = selectedOptions.length > 3;

    return (
        <div className="multi-select-dropdown" ref={dropdownRef}>
            <label className="multi-select-label">{label}</label>

            {description && (
                <div className="multi-select-description">
                    <small>{description}</small>
                </div>
            )}

            <div className={`multi-select-container ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}>
                <div className="multi-select-trigger" onClick={() => !disabled && setIsOpen(!isOpen)}>
                    <div className="selected-display">
                        {loading ? (
                            <span className="loading-text">Loading options...</span>
                        ) : selectedOptions.length === 0 ? (
                            <span className="placeholder-text">{placeholder}</span>
                        ) : selectedOptions.length === 1 ? (
                            <span className="selected-text">{pretty(selectedOptions[0])}</span>
                        ) : (
                            <span className="selected-text">{selectedOptions.length} items selected</span>
                        )}
                    </div>
                    <div className="dropdown-arrow">{isOpen ? '▲' : '▼'}</div>
                </div>

                {isOpen && !disabled && (
                    <div className="multi-select-dropdown-menu">
                        {options.length > 0 && (
                            <div className="dropdown-actions">
                                <button type="button" className="action-button" onClick={handleClearAll}>
                                    Clear All
                                </button>
                            </div>
                        )}

                        <div className="dropdown-options">
                            {options.length === 0 ? (
                                <div className="no-options">
                                    <strong>No options available</strong>
                                    <div>No options to display</div>
                                </div>
                            ) : (
                                options.map(option => {
                                    const disabledOpt = optionDisabled(option);
                                    const selected = selectedOptions.includes(option);
                                    return (
                                        <div
                                            key={option}
                                            className={`dropdown-option ${selected ? 'selected' : ''} ${disabledOpt ? 'disabled' : ''}`}
                                            onClick={() => handleOptionToggle(option)}
                                            aria-disabled={disabledOpt}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => {}}
                                                className="option-checkbox"
                                                disabled={disabledOpt}
                                            />
                                            <span className="option-label">{pretty(option)}</span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>

            {selectedOptions.length > 0 && (
                <div className="selected-tags">
                    {tagsToShow.map(option => (
                        <span key={option} className="selected-tag">
              {pretty(option)}
                            <button
                                type="button"
                                className="remove-tag"
                                onClick={() => handleOptionToggle(option)}
                                disabled={disabled || optionDisabled(option)}
                            >
                ×
              </button>
            </span>
                    ))}

                    {hasMoreTags && !showAllTags && (
                        <button type="button" className="expand-tags-button" onClick={() => setShowAllTags(true)}>
                            ...more ({selectedOptions.length - 3})
                        </button>
                    )}

                    {showAllTags && hasMoreTags && (
                        <button type="button" className="collapse-tags-button" onClick={() => setShowAllTags(false)}>
                            hide
                        </button>
                    )}
                </div>
            )}

            {errorMessage && <div className="multi-select-error">{errorMessage}</div>}
        </div>
    );
};

export default MultiSelectDropdown;
